import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitHandler } from 'react-hook-form';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';

import { Button } from 'components/Button';
import { DeletionProgressToast } from 'components/DeletionProgressToast';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { AvailableTabs } from 'constants/SidebarTabs';
import { useToast } from 'hooks/useToaster';
import { cancelCarrierEmail } from 'lib/api/cancelCarrierEmail';
import { undoCancelCarrierEmail } from 'lib/api/undoCancelCarrierEmail';
import { updateCarrierEmail } from 'lib/api/updateCarrierEmail';
import { NormalizedLoad } from 'types/Load';
import { PendingCarrierEmails, PendingEmail } from 'types/PendingOutboxEmail';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import CarrierEmailItem from './CarrierEmailItem';
import {
  getHeaderText,
  getMilestoneIcon,
} from './helpers/carrierEmailsHelpers';

dayjs.extend(utc);
dayjs.extend(timezone);

type CarrierEmailsProps = {
  normalizedLoad: NormalizedLoad;
  carrierEmails: PendingCarrierEmails;
  setTab: React.Dispatch<React.SetStateAction<AvailableTabs>>;
};

export default function CarrierEmails({
  normalizedLoad,
  carrierEmails,
  setTab,
}: CarrierEmailsProps) {
  const [expandedEmails, setExpandedEmails] = useState<{
    [key: number]: boolean;
  }>({});
  const [deletedEmails, setDeletedEmails] = useState<number[]>([]);
  const [undoableEmails, setUndoableEmails] = useState<number[]>([]);
  const [deleteTimeouts, setDeleteTimeouts] = useState<{
    [key: number]: NodeJS.Timeout;
  }>({});
  const [toastIds, setToastIds] = useState<{
    [key: number]: string;
  }>({});

  const { toast, dismiss } = useToast();
  const [loadingEmailIds, setLoadingEmailIds] = useState<Set<number>>(
    new Set()
  );
  const [updatedTimestamps, setUpdatedTimestamps] = useState<{
    [emailId: number]: string;
  }>({});

  // Refs to always have the latest snapshot of timers without re-registering effects
  const deleteTimeoutsRef = useRef<{ [key: number]: NodeJS.Timeout }>({});
  const toastIdsRef = useRef<{ [key: number]: string }>({});
  const handleUndoCancelClickRef =
    useRef<Maybe<(emailId: number) => Promise<void>>>(null);

  // Ref for Jodit editors (one per email)
  const joditRefsRef = useRef<{ [key: number]: Maybe<IJodit> }>({});

  const noCarrierEmails: boolean = Object.values(carrierEmails).every(
    (emailArray) => emailArray.length === 0
  );

  const onSubmit: SubmitHandler<PendingEmail> = async (data) => {
    setLoadingEmailIds((prev) => new Set(prev).add(data.id));

    const reqData = {
      recipients: data.recipients,
      cc: data.cc,
      subject: data.subject,
      body: data.body,
      scheduleSend: data.scheduleSend,
    };

    const res = await updateCarrierEmail(data.id, reqData);

    if (res.isOk()) {
      // Update the timestamp in local state to reflect the new scheduled time
      if (data.scheduleSend) {
        const updatedTimestamp = dayjs(data.scheduleSend).utc().toISOString();
        setUpdatedTimestamps((prev) => ({
          ...prev,
          [data.id]: updatedTimestamp,
        }));
      }

      toast({
        description: res.value.message,
        variant: 'success',
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }

    setLoadingEmailIds((prev) => {
      const next = new Set(prev);
      next.delete(data.id);
      return next;
    });
  };

  // Memoize sorting and grouping operations
  const sortedEmails = useMemo(() => {
    return Object.entries(carrierEmails).map(([milestone, emailArray]) => {
      const sortedArray = [...emailArray].sort(
        (a: PendingEmail, b: PendingEmail) => {
          const timestampA = updatedTimestamps[a.id] || a.timestamp;
          const timestampB = updatedTimestamps[b.id] || b.timestamp;
          const dateA = dayjs.utc(timestampA).local();
          const dateB = dayjs.utc(timestampB).local();
          return dateA.diff(dateB);
        }
      );

      // Group emails by date
      const groupedEmails: { [date: string]: PendingEmail[] } = {};
      sortedArray.forEach((email: PendingEmail) => {
        const timestamp = updatedTimestamps[email.id] || email.timestamp;
        const date = dayjs.utc(timestamp).local().format('MM/DD');
        if (!groupedEmails[date]) {
          groupedEmails[date] = [];
        }
        groupedEmails[date].push(email);
      });

      return [milestone, groupedEmails] as [
        string,
        { [date: string]: PendingEmail[] },
      ];
    });
  }, [carrierEmails, updatedTimestamps]);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleEmailClick = useCallback((emailId: number) => {
    setExpandedEmails((prevState) => ({
      ...prevState,
      [emailId]: !prevState[emailId],
    }));
  }, []);

  // Batch state updates to reduce re-renders
  const setDeletedEmailWithDelay = useCallback(
    (emailId: number, delay: number) => {
      setUndoableEmails((prev) => [...prev, emailId]);

      const timeoutId = setTimeout(() => {
        // Batch multiple state updates together
        setUndoableEmails((prev) => prev.filter((id) => id !== emailId));
        setDeletedEmails((prevDeletedEmails) => [
          ...prevDeletedEmails,
          emailId,
        ]);
      }, delay);

      setDeleteTimeouts((prev) => {
        const next = { ...prev, [emailId]: timeoutId };
        deleteTimeoutsRef.current = next;
        return next;
      });
    },
    []
  );

  const handleUndoCancelClick = useCallback(
    async (emailId: number) => {
      // Clear the timeout first to prevent the email from being added to deletedEmails
      const timeoutId =
        deleteTimeouts[emailId] || deleteTimeoutsRef.current[emailId];
      if (timeoutId) {
        clearTimeout(timeoutId);
        setDeleteTimeouts((prev) => {
          const newTimeouts = { ...prev };
          delete newTimeouts[emailId];
          deleteTimeoutsRef.current = newTimeouts;
          return newTimeouts;
        });
      }

      setUndoableEmails((prev) => prev.filter((id) => id !== emailId));
      setDeletedEmails((prev) => prev.filter((id) => id !== emailId));

      // Dismiss the progress toast
      const currentToastId = toastIds[emailId] || toastIdsRef.current[emailId];
      if (currentToastId) {
        dismiss(currentToastId);
        setToastIds((prev) => {
          const newToastIds = { ...prev };
          delete newToastIds[emailId];
          toastIdsRef.current = newToastIds;

          return newToastIds;
        });
      }

      const res = await undoCancelCarrierEmail(emailId);

      if (res.isOk()) {
        toast({
          description: 'Restored scheduled email.',
          variant: 'success',
        });

        return;
      }
      // If the API call failed, re-add to undoableEmails so user can try again
      setUndoableEmails((prev) => [...prev, emailId]);
      toast({
        description: 'Failed to restore email.',
        variant: 'destructive',
      });
    },
    [deleteTimeouts, toastIds, dismiss, toast]
  );

  // Keep the ref in sync with the latest callback
  useEffect(() => {
    handleUndoCancelClickRef.current = handleUndoCancelClick;
  }, [handleUndoCancelClick]);

  const handleCancelClick = useCallback(
    async (emailId: number) => {
      const res = await cancelCarrierEmail(emailId);

      if (res.isOk()) {
        const toastResult = toast({
          description: (
            <DeletionProgressToast
              message={res.value.message}
              objectName='email'
              duration={5000}
              onUndo={() => {
                handleUndoCancelClickRef.current?.(emailId);
              }}
            />
          ),
          variant: 'warning',
        });

        setToastIds((prev) => {
          const next = { ...prev, [emailId]: toastResult.id };
          toastIdsRef.current = next;
          return next;
        });

        setDeletedEmailWithDelay(emailId, 5000);
      } else {
        toast({
          description: res.error.message,
          variant: 'destructive',
        });
      }
    },
    [toast, setDeletedEmailWithDelay]
  );

  // Keep the refs in sync with latest state
  useEffect(() => {
    deleteTimeoutsRef.current = deleteTimeouts;
  }, [deleteTimeouts]);

  useEffect(() => {
    toastIdsRef.current = toastIds;
  }, [toastIds]);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      Object.values(deleteTimeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  if (noCarrierEmails) {
    return (
      <Flex direction='col' align='center' gap='sm' className='px-2 mx-4'>
        <Typography variant='body-sm' className='mb-4'>
          No emails scheduled for this load yet. You can configure them in the
          Track & Trace tab under Contact Carrier section.
        </Typography>
        <Button
          className='text-sm h-8'
          buttonNamePosthog={ButtonNamePosthog.GoToTrackAndTraceTab}
          onClick={() => setTab(AvailableTabs.TrackAndTrace)}
        >
          Go to Track & Trace
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction='col' gap='xs' className='mx-4 mb-1 -mt-1'>
        <Typography variant='body-sm'>
          View and manage emails to carrier.
        </Typography>
        <Typography variant='body-xs' className='italic' textColor='muted'>
          Note: All timestamps are in your local timezone.
        </Typography>
      </Flex>

      <ol className='relative ms-3 px-1'>
        {sortedEmails.map(([milestone, groupedEmails]) => {
          const allEmailsForMilestone = Object.values(groupedEmails).flat();
          const activeEmails = allEmailsForMilestone.filter(
            (email) =>
              !deletedEmails.includes(email.id) ||
              undoableEmails.includes(email.id)
          );

          return (
            <li
              key={milestone}
              className='relative mt-2 mb-4 border-s border-neutral-400 ps-3 pb-1'
            >
              <Typography
                variant='body-sm'
                className='absolute flex items-center w-5 h-5 bg-neutral-50 rounded-full -start-2.5'
              >
                {getMilestoneIcon(milestone, activeEmails)}
              </Typography>
              <Typography
                variant='h5'
                className='flex mb-2 ml-1'
                weight='semibold'
              >
                {getHeaderText(milestone)}
              </Typography>

              {activeEmails.length === 0 && (
                <Flex direction='col' gap='sm'>
                  <Typography variant='body-xs' textColor='muted'>
                    No emails scheduled for this event.
                  </Typography>
                  <Button
                    className='text-xs h-8 px-3'
                    buttonNamePosthog={ButtonNamePosthog.GoToTrackAndTraceTab}
                    onClick={() => {
                      setTab(AvailableTabs.TrackAndTrace);
                      // Scroll up slightly to center the content on the CarrierCheckIn section
                      setTimeout(() => {
                        window.scrollTo({
                          top: window.scrollY - 160,
                          behavior: 'smooth',
                        });
                      }, 100);
                    }}
                  >
                    Schedule email with Track & Trace
                  </Button>
                </Flex>
              )}

              {Object.entries(groupedEmails).map(([date, emailGroup]) => (
                <React.Fragment key={date}>
                  {emailGroup.map((email) => {
                    // Don't filter out if it's in the undoable list (currently being undone)
                    const isUndoable = undoableEmails.includes(email.id);
                    const shouldHide =
                      deletedEmails.includes(email.id) && !isUndoable;

                    if (shouldHide) {
                      return <React.Fragment key={email.id}></React.Fragment>;
                    }

                    const isEmailLoading = loadingEmailIds.has(email.id);

                    return (
                      <CarrierEmailItem
                        key={email.id}
                        email={email}
                        timestampOverride={updatedTimestamps[email.id]}
                        isExpanded={!!expandedEmails[email.id]}
                        isUndoable={isUndoable}
                        loading={isEmailLoading}
                        normalizedLoad={normalizedLoad}
                        joditRefsRef={joditRefsRef}
                        onEmailClick={handleEmailClick}
                        onCancelClick={handleCancelClick}
                        onUndoCancelClick={handleUndoCancelClick}
                        onSubmit={onSubmit}
                        toast={toast}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </li>
          );
        })}
      </ol>
    </>
  );
}
